import React, { useEffect, useState, useRef } from 'react';
import { Snackbar, Alert, AlertColor, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useJobs } from '../contexts/JobsContext';
import { ExportJob, JobsAPI } from '../services/jobs-api';

interface Notification {
  id: string;
  jobId: string;
  message: string;
  severity: AlertColor;
}

export const JobNotifications: React.FC = () => {
  const { jobs } = useJobs();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const seenJobIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    // Check for newly completed/failed/cancelled jobs that haven't been seen
    jobs.forEach((job) => {
      // Skip running jobs or jobs that have already been seen
      if (job.status === 'running' || job.notificationSeen) {
        return;
      }

      // Check if we've already created a notification for this job
      if (seenJobIds.current.has(job.id)) {
        return;
      }

      // Mark this job as seen in our local ref
      seenJobIds.current.add(job.id);

      // Mark as seen on the backend
      JobsAPI.markJobSeen(job.id).catch(err => {
        console.error('Failed to mark job as seen:', err);
      });

      // Create notification
      let message = '';
      let severity: AlertColor = 'info';

      switch (job.status) {
        case 'completed':
          message = `Export completed: ${job.result?.exported.length || 0} items exported to ${job.targetEnvironment}`;
          severity = 'success';
          break;
        case 'failed':
          message = `Export failed: ${job.error || 'Unknown error'}`;
          severity = 'error';
          break;
        case 'cancelled':
          message = 'Export cancelled';
          severity = 'warning';
          break;
      }

      const notification: Notification = {
        id: `notification-${job.id}`,
        jobId: job.id,
        message,
        severity,
      };

      setNotifications(prev => [...prev, notification]);

      // Auto-remove after 8 seconds
      setTimeout(() => {
        setNotifications(prev => prev.filter(n => n.id !== notification.id));
      }, 8000);
    });
  }, [jobs]);

  const handleClose = (notificationId: string) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  };

  return (
    <>
      {notifications.map((notification, index) => (
        <Snackbar
          key={notification.id}
          open={true}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          sx={{ 
            bottom: { xs: 24 + (index * 70), sm: 24 + (index * 70) },
          }}
        >
          <Alert
            severity={notification.severity}
            action={
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={() => handleClose(notification.id)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
            sx={{ width: '100%' }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      ))}
    </>
  );
};
